/**
 * draft-reducer — the canonical agent-patch reducer for the routine editor (P12 §6.3, T-FE2).
 *
 * `applyRoutineDraftPatch(draft, patch)` applies a single persisted-vocabulary
 * {@link RoutineDraftPatch} stage onto the flat {@link WorkflowDraft} view-model and reports which
 * editor {@link StepKey} the patch touched (for staleness propagation + the fill-in highlight).
 *
 * It reuses the shared mappers (`triggerToSchedule` for schedule↔trigger, `configToCondition` for
 * condition↔ConditionConfig) so an agent patch and a server-loaded workflow land in BYTE-IDENTICAL
 * draft state — one bijection, two entry points (load path + patch path).
 *
 * Pure: no React/hook deps, never mutates `draft`. `useRoutineDraft` (T-FE1) imports this as its
 * canonical reducer; `useWorkflowChat` (T-FE3) routes `routine_draft` SSE events through it.
 *
 * Destination is always `slack` — the `agent`-as-terminal-node was deferred; see
 * `docs/agent-routines-future.md`.
 */
import type { RoutineDraftPatch } from '@server/types';

import { configToCondition, triggerToSchedule } from './mappers';
import type { StepKey, WorkflowDraft } from './types';

/** Result of applying a patch: the next flat draft + the {@link StepKey} it touched (if any). */
export interface ApplyRoutineDraftPatchResult {
  draft: WorkflowDraft;
  /** The editor step whose staleness/highlight this patch propagates. Absent for `name`/`schedule`. */
  editedStep?: StepKey;
}

/**
 * Apply one persisted-vocabulary {@link RoutineDraftPatch} onto the flat {@link WorkflowDraft}.
 *
 * - `name` / `schedule` → meta fields, no `editedStep` (the meta chrome isn't a chain step).
 * - `query` → flat `sql`/`warehouseId` (each optional so a patch can fill one field at a time), step `query`.
 * - `logic` → `condition`; `null` clears it (persisted→view-model flatten), step `condition`.
 * - `destination` → flat `channel`/`template` (slack); each optional so a patch can fill one field
 *   at a time; step `slack` (the terminal step key).
 */
export function applyRoutineDraftPatch(draft: WorkflowDraft, patch: RoutineDraftPatch): ApplyRoutineDraftPatchResult {
  switch (patch.stage) {
    case 'name':
      return { draft: { ...draft, name: patch.name } };
    case 'description':
      return { draft: { ...draft, description: patch.description } };
    case 'schedule':
      return { draft: { ...draft, schedule: triggerToSchedule(patch.trigger) } };
    case 'query':
      return {
        draft: {
          ...draft,
          ...(patch.warehouseId !== undefined ? { warehouseId: patch.warehouseId } : {}),
          ...(patch.sql !== undefined ? { sql: patch.sql } : {}),
        },
        editedStep: 'query',
      };
    case 'logic':
      // `null` clears the condition; otherwise map the persisted ConditionConfig → editor condition.
      return {
        draft: { ...draft, condition: patch.condition === null ? null : configToCondition(patch.condition) },
        editedStep: 'condition',
      };
    case 'destination':
      // Destination is always slack: set channel + template; each optional so a patch can fill
      // one field at a time without clobbering the other.
      return {
        draft: {
          ...draft,
          ...(patch.channel !== undefined ? { channel: patch.channel } : {}),
          ...(patch.template !== undefined ? { template: patch.template } : {}),
        },
        editedStep: 'slack',
      };
    default:
      // Exhaustiveness guard — a new stage must be handled here.
      patch satisfies never;
      return { draft };
  }
}
